/**
 * Slovník parametrů veřejného webu (vize podnety-tymu-2026-10, řez 02, F5.2): JEDINÉ místo,
 * kde žije překlad mezi VNITŘNÍMI (anglickými) názvy a hodnotami parametrů, se kterými
 * pracuje kód webu i API, a WEBOVÝMI (českými) v adrese.
 *
 * ── Hrana ──
 *
 * Překládá se jen na hraně webu. `serverUrl` (web) vybere z adresy webové názvy plochy
 * a přes `webParamsToInternal` je předá loaderům a parserům, které čtou jen vnitřní názvy
 * (`tripFilters.ts` zůstává anglický, čte ho i API). Každý stavitel odkazu bere vnitřní
 * vstup a vrací výstup `internalParamsToWeb`. Nad výstupem `serverUrl` nikdo
 * `webParamsToInternal` nevolá: dvojí překlad by zahodil každý vnitřní název odlišný od
 * webového. Databáze, API a jeho parametry zůstávají anglicky.
 *
 * ── Pravidla ──
 *
 * Čtení (`webParamsToInternal`): webový název plochy se přeloží na vnitřní a známá webová
 * hodnota na vnitřní (webový název má přednost). Neznámá hodnota parametru s tabulkou
 * hodnot se zahodí (u čárkového seznamu po položkách). Zahodí se i parametr, jehož název
 * je vnitřním názvem plochy a NENÍ zároveň jejím webovým (identity z `CZECH_NAMES` tedy
 * projdou), a zaniklé parametry plochy (výpis zájezdů `month`, `holiday`, `budget`, recenze
 * `page`): staré anglické adresy web čte jako neznámé a nepřesměrovává je (Ne-cíl vize).
 * Průchozí (`WEB_PARAMS_PASSTHROUGH`) a ostatní parametry zůstanou beze změny.
 *
 * Zápis (`internalParamsToWeb`): vnitřní název a hodnota se přeloží na webové (čárkový
 * seznam po položkách), ostatní zůstane. Obě funkce zachovávají pořadí parametrů.
 *
 * Datum, čísla a slugy destinací se nepřekládají.
 *
 * ── Směr importů ──
 *
 * Modul importuje jen `routes.ts`, `webSections.ts`, `tripFilters.ts`, `tripBuckets.ts`,
 * `czLabels.ts`, `types` a `utils`; žádný z nich (ani nic, co importují) tenhle modul
 * neimportuje. Opačná hrana by s konstantami na úrovni modulu (`WEB_PARAM_SURFACES` volá
 * `routes.*()`) skončila `ReferenceError` (TDZ) podle pořadí načtení. Konzumenti
 * (`htmlCache.ts`, `tripWizard.ts`, `customInquiry.ts`, `offerUrl.ts`,
 * `inspirationListing.ts`, `trackingUrl.ts`) ho importovat smějí, on je ne. Proto tu jsou
 * klíč kroku průvodce (`krok`) a obcházecí `nc` literálem; `webParams.test.ts` je
 * porovná s `WIZARD_STEP_PARAM` a `HTML_CACHE_BYPASS_PARAM`.
 *
 * Bez zod: modul je i v klientském vstupu (`client.ts`).
 */
import {
  ACTIVITY_TYPE_LABELS_CZ,
  BLOG_CATEGORY_LABELS_CZ,
  CONTACT_TOPIC_LABELS_CZ,
  DIFFICULTY_LABELS_CZ,
  HOTEL_FEATURE_LABELS_CZ,
  HOTEL_MEAL_PLAN_LABELS_CZ,
  TRIP_STYLE_LABELS_CZ,
} from "./czLabels";
import {
  INSPIRATION_TRIPS_EXPAND_PARAM,
  OFFER_TYPE_PATH_SLUGS,
  REVIEWS_DESTINATION_QUERY_PARAM,
  REVIEWS_LEGACY_PAGE_PARAM,
  TRIP_DETAIL_QUERY,
  routePrefixes,
  routes,
} from "./routes";
import type { DurationBucketKey } from "./tripBuckets";
import type { TripFilterParamKey, TripListingView, VirtualDuration } from "./tripFilters";
import type { DestinationRegion, TripType } from "./types";
import { slugify } from "./utils";
import { CONTINENT_SLUGS_CZ } from "./webSections";

/** Plocha webu se svými parametry. */
export interface WebParamSurface extends WebPathEntry {
  /** Cesta z `routes.*()` (bez koncového lomítka), stejně jako `SERVER_QUERY_PARAMS`. */
  path: string;
  /** Vnitřní název → webový název. */
  names: Readonly<Record<string, string>>;
  /** Vnitřní název → (vnitřní hodnota → webová hodnota). */
  values?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** Vnitřní názvy s čárkovým seznamem hodnot (překládají se po položkách). */
  csv?: readonly string[];
}

/** Sledovací a obcházecí parametry: nepřekládají se nikdy a nejsou názvem žádné plochy. */
export const WEB_PARAMS_PASSTHROUGH: readonly (string | RegExp)[] = [
  /^utm_/,
  "gclid",
  "fbclid",
  "msclkid",
  "nc",
];

/** Webové hodnoty, které jsou shodné s vnitřními (česky i anglicky stejné). */
export const SAME_IN_CZECH: readonly string[] = ["wellness", "1-10", "11-14", "15-21"];

/** Názvy, které už jsou české: `names[x] === x` (identita). */
export const CZECH_NAMES: readonly string[] = [
  "destinace",
  "termin",
  "krok",
  "zdroj",
  "strana",
  "mesic",
  "klienti",
  "google",
  "zajezdy",
  "inspirace",
  "skupina",
];

// ── Tabulky hodnot ──────────────────────────────────────────────────────────

/** Webové hodnoty z českých popisků: `slugify(popisek)`. */
function slugValues<K extends string>(labels: Readonly<Record<K, string>>): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const key of Object.keys(labels) as K[]) out[key] = slugify(labels[key]);
  return out;
}

const FLAG_VALUES = { true: "ano" } as const;

const TRIP_TYPE_VALUES: Readonly<Record<TripType, string>> = OFFER_TYPE_PATH_SLUGS;

const REGION_VALUES: Readonly<Record<DestinationRegion, string>> = {
  northern: "sever",
  central: "stred",
  southern: "jih",
};

/**
 * Délka: buckety jsou číselné rozsahy a zůstávají (`SAME_IN_CZECH`), jen otevřený konec
 * česky (`slugify` popisku by pomlčku smazal: `1–10 dní` → `110-dni`). Virtuální délky
 * (`VirtualDuration`, týž parametr) česky.
 */
const DURATION_VALUES: Readonly<Record<DurationBucketKey | VirtualDuration, string>> = {
  "1-10": "1-10",
  "11-14": "11-14",
  "15-21": "15-21",
  "22-plus": "22-a-vice",
  day: "jednodenni",
  multi: "vicedenni",
};

/** Řazení výpisu (`?sort=`): krátké české slovo; `recommended` = výchozí „Doporučené“. */
const SORT_VALUES: Readonly<Record<string, string>> = {
  recommended: "doporucene",
  "price-asc": "nejlevnejsi",
  "price-desc": "nejdrazsi",
  "duration-asc": "nejkratsi",
  "duration-desc": "nejdelsi",
};

const VIEW_VALUES: Readonly<Record<TripListingView, string>> = {
  grid: "mrizka",
  map: "mapa",
  routes: "trasy",
};

// ── Názvy ───────────────────────────────────────────────────────────────────

/**
 * Webové názvy jmenného prostoru filtrů katalogu. `Record<TripFilterParamKey, …>`: nový
 * filtr bez webového názvu shodí typecheck. Pevné (kritéria a scénáře je čtou):
 * `destinace`, `kontinent`, `styl`, `typ`, `od`, `do`, `delka`; ostatní = `slugify`
 * českého popisku filtru.
 */
const TRIP_FILTER_WEB_NAMES: Readonly<Record<TripFilterParamKey, string>> = {
  destination: "destinace",
  continent: "kontinent",
  region: "oblast",
  tripType: "typ",
  hotels: "hotely",
  tripStyle: "styl",
  difficultyLevel: "narocnost",
  priceMin: "cena-od",
  priceMax: "cena-do",
  durationMin: "dni-od",
  durationMax: "dni-do",
  duration: "delka",
  mealPlan: "strava",
  hotelStars: "trida-hotelu",
  hotelFeature: "zamereni-hotelu",
  dateFrom: "od",
  dateTo: "do",
  dateTolerance: "tolerance",
  bestMonth: "nejlepsi-pocasi",
  hasCzechGuide: "cesky-pruvodce",
  isFeatured: "doporucene",
  isLastMinute: "last-minute",
  isForWomen: "pro-zeny",
  guide: "pruvodce",
  search: "hledat",
};

const TRIP_FILTER_VALUES: Readonly<Partial<Record<TripFilterParamKey, Record<string, string>>>> = {
  continent: CONTINENT_SLUGS_CZ,
  region: REGION_VALUES,
  tripType: TRIP_TYPE_VALUES,
  hotels: FLAG_VALUES,
  tripStyle: slugValues(TRIP_STYLE_LABELS_CZ),
  difficultyLevel: slugValues(DIFFICULTY_LABELS_CZ),
  duration: DURATION_VALUES,
  mealPlan: slugValues(HOTEL_MEAL_PLAN_LABELS_CZ),
  hotelFeature: slugValues(HOTEL_FEATURE_LABELS_CZ),
  hasCzechGuide: FLAG_VALUES,
  isFeatured: FLAG_VALUES,
  isLastMinute: FLAG_VALUES,
  isForWomen: FLAG_VALUES,
};

/** Vícehodnotové dimenze, které parser filtrů čte jako čárkový seznam (`getCsv`). */
const TRIP_FILTER_CSV: readonly TripFilterParamKey[] = [
  "destination",
  "tripType",
  "tripStyle",
  "difficultyLevel",
  "duration",
  "mealPlan",
  "hotelStars",
  "hotelFeature",
];

const PAGE_NAME = { page: "strana" } as const;

/** Identita: název, který je česky už dnes (`CZECH_NAMES`). */
const identity = (...names: string[]): Record<string, string> =>
  Object.fromEntries(names.map((name) => [name, name]));

/** Výběr z názvů filtrů (průvodce a poptávka na míru sdílejí webové názvy výpisu). */
const filterNames = (...keys: TripFilterParamKey[]): Record<string, string> =>
  Object.fromEntries(keys.map((key) => [key, TRIP_FILTER_WEB_NAMES[key]]));

/** Klíč kroku průvodce (`WIZARD_STEP_PARAM` v `tripWizard.ts`, který sem importovat nejde). */
const WIZARD_STEP_NAME = "krok";

/** Plochy průvodce: klíče kroků (`WIZARD_STEPS[].key`) a krok. */
const wizardSurface = (path: string): WebParamSurface => ({
  path,
  match: "exact",
  names: { ...filterNames("tripType", "duration", "bestMonth"), ...identity(WIZARD_STEP_NAME) },
  values: { tripType: TRIP_TYPE_VALUES, duration: DURATION_VALUES },
  csv: ["tripType", "duration"],
});

/**
 * Cesta bez koncového lomítka, kořen `/` zůstává. JEDINÁ normalizace cesty pro plochy
 * slovníku i pro tabulku klíče keše (`htmlCachePathname` ji jen pojmenovává).
 */
export function webPathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/** Položka tabulky po cestách: plocha slovníku i řádek inventury keše HTML. */
export interface WebPathEntry {
  path: string;
  /** `exact` = jen `path`; `prefix` = `path` i cesty pod ní; `below` = jen cesty pod ní. */
  match: "exact" | "prefix" | "below";
}

function pathEntryMatches(entry: WebPathEntry, path: string): boolean {
  if (path === entry.path) return entry.match !== "below";
  return entry.match !== "exact" && path.startsWith(`${entry.path}/`);
}

/**
 * Položka tabulky pro cestu: koncové lomítko nerozhoduje, z více shod platí nejdelší
 * `path`, bez shody `undefined`. JEDINÝ výběr cesty pro slovník (`webSurfaceFor`) i klíč
 * keše (`serverSearchParams` v `htmlCache.ts`): dvě kopie by téže cestě přiřadily jinou
 * plochu slovníku než položku keše a překlad by zahodil, co klíč vybral.
 */
export function pathEntryFor<T extends WebPathEntry>(
  entries: readonly T[],
  pathname: string,
): T | undefined {
  const path = webPathname(pathname);
  let best: T | undefined;
  for (const entry of entries) {
    if (!pathEntryMatches(entry, path)) continue;
    if (!best || entry.path.length > best.path.length) best = entry;
  }
  return best;
}

const TRIPS_SURFACE: WebParamSurface = {
  path: routes.zajezdy(),
  match: "prefix",
  names: {
    ...TRIP_FILTER_WEB_NAMES,
    view: "zobrazeni",
    sort: "razeni",
    ...PAGE_NAME,
  },
  values: { ...TRIP_FILTER_VALUES, view: VIEW_VALUES, sort: SORT_VALUES },
  csv: TRIP_FILTER_CSV,
};

const REVIEWS_SURFACE: WebParamSurface = {
  path: routes.recenze(),
  match: "prefix",
  names: identity(REVIEWS_DESTINATION_QUERY_PARAM, "klienti", "google"),
};

/** Plochy webu v pořadí tabulky klíče keše (`SERVER_QUERY_PARAMS`). */
export const WEB_PARAM_SURFACES: readonly WebParamSurface[] = [
  {
    path: routes.blog(),
    match: "exact",
    names: { category: "kategorie", ...PAGE_NAME },
    values: { category: slugValues(BLOG_CATEGORY_LABELS_CZ) },
  },
  REVIEWS_SURFACE,
  {
    path: routes.recenzeKarty(),
    match: "exact",
    names: identity("zdroj", REVIEWS_DESTINATION_QUERY_PARAM, "strana"),
  },
  {
    path: routes.kdyVyrazit(),
    match: "exact",
    names: identity("mesic"),
  },
  {
    path: routes.inspirace(),
    match: "exact",
    names: { type: "typ", destination: TRIP_FILTER_WEB_NAMES.destination, ...PAGE_NAME },
    values: { type: slugValues(ACTIVITY_TYPE_LABELS_CZ) },
  },
  {
    // Detail inspirace: `zajezdy` je česky už dnes, hodnoty (`obsahuji`, `dalsi`) tabulku
    // nemají a projdou beze změny.
    path: routes.inspirace(),
    match: "below",
    names: identity(INSPIRATION_TRIPS_EXPAND_PARAM),
  },
  {
    path: routes.inspiraceZajezdyKarty(),
    match: "exact",
    names: identity("inspirace", "skupina"),
  },
  TRIPS_SURFACE,
  wizardSurface(routes.kviz()),
  wizardSurface(routes.hledacInspiraci()),
  {
    path: routes.zajezdNaMiru(),
    match: "exact",
    names: {
      ...filterNames("destination", "dateFrom", "dateTo", "tripType"),
      from: "odkud",
    },
    values: { tripType: TRIP_TYPE_VALUES },
  },
  {
    path: routes.hotely(),
    match: "exact",
    names: filterNames("destination"),
    csv: ["destination"],
  },
  {
    // Téma kontaktního formuláře (předvybraná volba, `pages/kontakt.astro`); hodnota volby
    // (`subject` formuláře) zůstává anglická, česky je jen adresa.
    path: routes.kontakt(),
    match: "exact",
    names: { topic: "tema" },
    values: { topic: slugValues(CONTACT_TOPIC_LABELS_CZ) },
  },
  {
    // Detail zájezdu: čte ho jen skript prohlížeče, přímo podle `TRIP_DETAIL_QUERY`.
    path: webPathname(routePrefixes.zajezd),
    match: "prefix",
    names: {
      dateFrom: TRIP_DETAIL_QUERY.dateFrom,
      dateTo: TRIP_DETAIL_QUERY.dateTo,
      [TRIP_DETAIL_QUERY.departure]: TRIP_DETAIL_QUERY.departure,
    },
  },
];

/** Zaniklé parametry plochy: čtení je zahodí jako neznámé (F5.2, bez přesměrování). */
const RETIRED_PARAMS: ReadonlyMap<WebParamSurface, ReadonlySet<string>> = new Map([
  [TRIPS_SURFACE, new Set(["month", "holiday", "budget"])],
  [REVIEWS_SURFACE, new Set([REVIEWS_LEGACY_PAGE_PARAM])],
]);

// ── Index ploch ─────────────────────────────────────────────────────────────

const hasOwn = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

interface SurfaceIndex {
  webToInternal: ReadonlyMap<string, string>;
  internalNames: ReadonlySet<string>;
  retired: ReadonlySet<string>;
  /** Vnitřní název → (vnitřní hodnota → webová hodnota). */
  valuesForward: ReadonlyMap<string, ReadonlyMap<string, string>>;
  /** Vnitřní název → (webová hodnota → vnitřní hodnota). */
  valuesBack: ReadonlyMap<string, ReadonlyMap<string, string>>;
  csv: ReadonlySet<string>;
}

const SURFACE_INDEX: ReadonlyMap<WebParamSurface, SurfaceIndex> = new Map(
  WEB_PARAM_SURFACES.map((surface) => [
    surface,
    {
      webToInternal: new Map(
        Object.entries(surface.names).map(([internal, web]) => [web, internal]),
      ),
      internalNames: new Set(Object.keys(surface.names)),
      retired: RETIRED_PARAMS.get(surface) ?? new Set<string>(),
      valuesForward: new Map(
        Object.entries(surface.values ?? {}).map(([internal, table]) => [
          internal,
          new Map(Object.entries(table)),
        ]),
      ),
      valuesBack: new Map(
        Object.entries(surface.values ?? {}).map(([internal, table]) => [
          internal,
          new Map(Object.entries(table).map(([value, web]) => [web, value])),
        ]),
      ),
      csv: new Set(surface.csv ?? []),
    },
  ]),
);

/** Plocha cesty (výběr `pathEntryFor`), bez shody `null`. */
export function webSurfaceFor(pathname: string): WebParamSurface | null {
  return pathEntryFor(WEB_PARAM_SURFACES, pathname) ?? null;
}

/** Webový název vnitřního parametru na cestě; parametr mimo slovník plochy beze změny. */
export function webParamName(pathname: string, internal: string): string {
  const surface = webSurfaceFor(pathname);
  return surface && hasOwn(surface.names, internal) ? surface.names[internal] : internal;
}

/** Hodnota přes tabulku; `null` = čtení ji zahodí. Bez tabulky beze změny. */
function translateValue(
  value: string,
  table: ReadonlyMap<string, string> | undefined,
  csv: boolean,
  unknown: "drop" | "keep",
): string | null {
  if (!table) return value;
  const one = (item: string): string | null =>
    table.get(item) ?? (unknown === "keep" ? item : null);
  if (!csv) return one(value);
  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(one)
    .filter((item): item is string => item !== null);
  return items.length > 0 ? items.join(",") : null;
}

/** Webové parametry adresy → vnitřní (pravidla čtení v hlavičce modulu). */
export function webParamsToInternal(pathname: string, params: URLSearchParams): URLSearchParams {
  const surface = webSurfaceFor(pathname);
  const index = surface ? SURFACE_INDEX.get(surface) : undefined;
  const out = new URLSearchParams();
  for (const [name, value] of params) {
    if (!index) {
      out.append(name, value);
      continue;
    }
    const internal = index.webToInternal.get(name);
    if (internal !== undefined) {
      const translated = translateValue(
        value,
        index.valuesBack.get(internal),
        index.csv.has(internal),
        "drop",
      );
      if (translated !== null) out.append(internal, translated);
      continue;
    }
    if (index.internalNames.has(name) || index.retired.has(name)) continue;
    out.append(name, value);
  }
  return out;
}

/** Vnitřní parametry → webové (pravidla zápisu v hlavičce modulu). */
export function internalParamsToWeb(pathname: string, params: URLSearchParams): URLSearchParams {
  const surface = webSurfaceFor(pathname);
  const index = surface ? SURFACE_INDEX.get(surface) : undefined;
  const out = new URLSearchParams();
  for (const [name, value] of params) {
    if (!surface || !index || !index.internalNames.has(name)) {
      out.append(name, value);
      continue;
    }
    const translated = translateValue(
      value,
      index.valuesForward.get(name),
      index.csv.has(name),
      "keep",
    );
    out.append(surface.names[name], translated ?? value);
  }
  return out;
}

/**
 * Adresa odkazu z VNITŘNÍCH parametrů: cesta + webový query string (`internalParamsToWeb`
 * nad touž cestou), prázdný dotaz = holá cesta. JEDINÁ koncovka stavitelů odkazů webu:
 * stavitel upraví vnitřní parametry a vrátí `webHref(cesta, parametry)`.
 */
export function webHref(pathname: string, internal: URLSearchParams): string {
  const query = internalParamsToWeb(pathname, internal).toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * Webová adresa prohlížeče (`window.location`) → VNITŘNÍ parametry, protějšek `webHref`
 * na vstupu. Kdo staví odkaz z řádku prohlížeče (auto-apply panelu, „Zrušit vše",
 * `listingSortHref`), jde přes tuhle funkci: webový název filtru by stavitel nechal jako
 * „ostatní" parametr a vedle něj zapsal nový, tedy dvakrát týž filtr. Nikdy ne nad
 * `data.url` ani nad `search` z props ostrova: ty jsou vnitřní už ze `serverUrl` a druhé
 * čtení by každý vnitřní název odlišný od webového zahodilo.
 */
export function internalParamsFromLocation(loc: {
  pathname: string;
  search: string;
}): URLSearchParams {
  return webParamsToInternal(loc.pathname, new URLSearchParams(loc.search));
}
